import { Link } from '@remix-run/react'
import { Alert, Button, Divider, Form, Input, Select, Typography } from 'antd'
import { WordpressFormValues } from '../..'

type Props = {
  form: ReturnType<typeof Form.useForm<WordpressFormValues>>[0]
  onCancel: () => void
  onFinish: (values: WordpressFormValues) => void
  isLoading?: boolean
  isError?: boolean
}

export const ConnectionForm: React.FC<Props> = ({
  form,
  onCancel,
  onFinish,
  isLoading,
  isError = false,
}) => {
  return (
    <>
      <Divider />
      {isError && (
        <Alert
          type="error"
          message={
            <>
              {
                'We couldn’t connect to your WordPress site. Please double-check the URL, username, and application password.'
              }
              <br />
              <Link
                to="https://help.marblism.com/en/articles/11570235-how-to-connect-penny-to-your-wordpress-website"
                target="_blank"
                className="text-blue-500 hover:text-blue-700 underline underline-offset-2"
                rel="noopener noreferrer"
              >
                🙋‍♂️ Trouble connecting? Follow these instructions
              </Link>
            </>
          }
          style={{ marginBottom: 6 }}
        />
      )}
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="WordPress URL"
          name="wordpressUrl"
          rules={[
            { required: true, message: 'Please enter your WordPress site URL' },
            { type: 'url', message: 'Must be a valid URL (https://...)' },
          ]}
        >
          <Input placeholder="https://mywebsite.com" />
        </Form.Item>

        <Form.Item
          label="Username"
          name="username"
          rules={[
            { required: true, message: 'Please enter your WordPress username' },
          ]}
        >
          <Input placeholder="admin@email.com" />
        </Form.Item>

        <Form.Item
          label="Application Password"
          name="applicationPassword"
          rules={[
            {
              required: true,
              message: 'Please enter your application password',
            },
            {
              min: 10,
              message:
                'Please enter a valid application password generated in your WordPress admin.',
            },
          ]}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              This is a special Application Password you can generate in your
              WordPress Admin Panel –{' '}
              <a
                href="https://www.paidmembershipspro.com/create-application-password-wordpress/"
                target="_blank"
                rel="noopener noreferrer"
              >
                How to generate an Application Password?
              </a>
            </Typography.Text>
          }
        >
          <Input.Password placeholder="Your Application Password" />
        </Form.Item>

        <Form.Item
          label="Publish Status"
          name="publishStatus"
          rules={[{ required: true }]}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              If you select Published, the article will be immediately live on
              your website. If you select Draft, you will need to approve it
              from your WordPress admin panel.
            </Typography.Text>
          }
        >
          <Select
            options={[
              { value: 'publish', label: 'Published' },
              { value: 'draft', label: 'Draft' },
            ]}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Save connection
          </Button>
        </div>
      </Form>
    </>
  )
}
